import React, { useEffect, useState } from 'react';
import api from '../api';
import { useParams, useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr;
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
}

export default function SupplierLedgerPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ledger, setLedger] = useState([]);
  const [summary, setSummary] = useState({ totalPurchases: 0, totalPayments: 0, outstanding: 0 });
  const [supplier, setSupplier] = useState(null);

  useEffect(() => {
    api.get(`/suppliers/${id}/ledger`).then(res => {
      setLedger(res.data.ledger || []);
      setSummary(res.data.summary || { totalPurchases: 0, totalPayments: 0, outstanding: 0 });
    });
    api.get('/suppliers').then(res => {
      setSupplier(res.data.find(s => s.SupplierID === parseInt(id)));
    });
  }, [id]);

  // Export handlers remain the same but use ledger for body, etc.
  const handleExportExcel = () => {
    const data = ledger.map(row => ({
      Date: formatDate(row.Date),
      Type: row.Type,
      Reference: row.Ref,
      'Debit (Bills)': Number(row.Debit || 0).toFixed(2),
      'Credit (Payments)': Number(row.Credit || 0).toFixed(2),
      Balance: Number(row.Balance || 0).toFixed(2),
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Ledger');
    const buf = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
    saveAs(new Blob([buf], { type: 'application/octet-stream' }), `${supplier?.Name || 'Supplier'}_Ledger.xlsx`);
  };

  const handleExportPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(15);
    doc.text(`Supplier Ledger: ${supplier?.Name || ''}`, 14, 16);
    doc.setFontSize(12);
    doc.text(
      `Total Purchases: ₹${summary.totalPurchases.toFixed(2)}   Total Payments: ₹${summary.totalPayments.toFixed(2)}   Outstanding: ₹${summary.outstanding.toFixed(2)}`,
      14,
      24
    );
    autoTable(doc, {
      startY: 30,
      head: [['Date', 'Type', 'Reference', 'Debit (Bills)', 'Credit (Payments)', 'Balance']],
      body: ledger.map(row => [
        formatDate(row.Date),
        row.Type,
        row.Ref,
        Number(row.Debit || 0).toFixed(2),
        Number(row.Credit || 0).toFixed(2),
        Number(row.Balance || 0).toFixed(2)
      ]),
    });
    doc.save(`${supplier?.Name || 'Supplier'}_Ledger.pdf`);
  };

  return (
    <div className="container mt-4">
      <h2>Supplier Ledger</h2>
      {supplier && (
        <div className="mb-2">
          <strong>{supplier.Name}</strong><br />
          GSTIN: {supplier.GSTIN || '-'}<br />
          Phone: {supplier.Phone || '-'}
        </div>
      )}

      <div className="mb-3">
 <span className="me-4"><strong>Total Purchases:</strong> ₹{Number(summary.totalPurchases || 0).toFixed(2)}</span>
<span className="me-4"><strong>Total Payments:</strong> ₹{Number(summary.totalPayments || 0).toFixed(2)}</span>
<span className="me-4"><strong>Outstanding:</strong> <span style={{ color: summary.outstanding > 0 ? 'red' : 'green' }}>₹{Number(summary.outstanding || 0).toFixed(2)}</span></span>

      </div>

      <div className="mb-3">
        <button className="btn btn-outline-success me-2" onClick={handleExportExcel}>Export to Excel</button>
        <button className="btn btn-outline-primary me-2" onClick={handleExportPDF}>Export to PDF</button>
      </div>

      <table className="table table-bordered table-striped">
        <thead>
          <tr>
            <th>Date</th>
            <th>Type</th>
            <th>Reference</th>
            <th>Debit (Bills)</th>
            <th>Credit (Payments)</th>
            <th>Balance</th>
          </tr>
        </thead>
        <tbody>
          {ledger.length === 0 && (
            <tr>
              <td colSpan="6" className="text-center text-muted">No transactions</td>
            </tr>
          )}
          {ledger.map((row, i) => (
            <tr key={i}>
              <td>{formatDate(row.Date)}</td>
              <td>{row.Type}</td>
              <td>{row.Ref}</td>
              <td>{Number(row.Debit || 0).toFixed(2)}</td>
              <td>{Number(row.Credit || 0).toFixed(2)}</td>
              <td>{Number(row.Balance || 0).toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <button className="btn btn-secondary mt-3" onClick={() => navigate('/suppliers')}>← Back to Suppliers</button>
    </div>
  );
}
